import {
	maxLength,
	minLength,
	parse,
	pipe,
	regex,
	string,
	ValiError
} from 'valibot';

/**
 * Card Name Validation Schema
 * - Only letters, numbers, spaces, "-", and "#"
 * - Maximum 25 characters
 */
export const cardNameSchema = pipe(
	string('Kortnamn måste vara en text'),
	minLength(1, 'Kortnamn får inte vara tomt'),
	maxLength(25, 'Kortnamn kan inte överstiga 25 tecken'),
	regex(/^[a-zA-Z0-9\s\-#åäöÅÄÖ]+$/, 'Kortnamn kan endast innehålla bokstäver, siffror, mellanslag, "-" och "#"')
)

/**
 * Card PIN Code Validation Schema
 * - Only numbers
 * - Exactly 4 digits
 */
export const cardPinCodeSchema = pipe(
	string('PIN-kod måste vara siffror'),
	minLength(4, 'PIN-kod måste innehålla exakt 4 siffror'),
	maxLength(4, 'PIN-kod måste innehålla exakt 4 siffror'),
	regex(/^\d{4}$/, 'PIN-kod får endast innehålla siffror')
)

/**
 * Validate card name and return error message if invalid
 */
export const validateCardName = (name: string): { valid: boolean; error?: string } => {
	try {
		parse(cardNameSchema, name)
		return { valid: true }
	} catch (error) {
		if (error instanceof ValiError) {
			const message = error.issues?.[0]?.message || 'Felaktig kortnamn'
			return { valid: false, error: message }
		}
		return { valid: false, error: 'Felaktig kortnamn' }
	}
}

/**
 * Validate card PIN and return error message if invalid
 * - Must be exactly 4 digits
 * - Cannot have all same digits (e.g., "0000", "1111", etc.)
 * - Cannot be a sequence (e.g., "1234", "9876", "0123")
 */
export const validateCardPin = (pin: string): { valid: boolean; error?: string } => {
	try {
		parse(cardPinCodeSchema, pin)
		
		// Check for all same digits (0000, 1111, etc.)
		if (/^(\d)\1{3}$/.test(pin)) {
			return { valid: false, error: 'PIN-kod kan inte innehålla samma siffra fyra gånger' }
		}

		// Check that PIN does not start with 0
		if (pin.startsWith('0')) {
			return { valid: false, error: 'PIN-kod kan inte börja med 0' }
		}
		
		// Check for ascending sequences (0123, 1234, 2345, etc.)
		const digits = pin.split('').map(Number)
		const isAscendingSequence = digits.every((digit, i) => i === 0 || digit === digits[i - 1] + 1)
		if (isAscendingSequence) {
			return { valid: false, error: 'PIN-kod kan inte vara en stigande talföljd (t.ex. 1234)' }
		}
		
		// Check for descending sequences (3210, 4321, 9876, etc.)
		const isDescendingSequence = digits.every((digit, i) => i === 0 || digit === digits[i - 1] - 1)
		if (isDescendingSequence) {
			return { valid: false, error: 'PIN-kod kan inte vara en fallande talföljd (t.ex. 9876)' }
		}
		
		return { valid: true }
	} catch (error) {
		if (error instanceof ValiError) {
			const message = error.issues?.[0]?.message || 'Felaktig PIN-kod'
			return { valid: false, error: message }
		}
		return { valid: false, error: 'Felaktig PIN-kod' }
	}
}
